/**
 * Far-field weather veil — the cheap "it's raining across the whole cell" cue that the
 * near-field particle box (precipFx) can't give without a fortune in particles.
 *
 * It's a single large cylinder pinned to the CAMERA and anchored to the planet's interpolated
 * pose (so it rides the surface, no drift/jitter — see `update`). The look is the game-standard
 * LAYERED-PARALLAX rain wall (Tatarchuk/ATI "City Rain", reused by Remember Me): instead of one
 * flat scrolling texture, a custom shader samples ONE motion-blurred drop texture FOUR times per
 * fragment, each layer at a different scale + scroll speed (near = large & fast, far = small &
 * slow). That speed/scale spread IS the depth cue — parallax without geometry, still one draw
 * call. A seamless low-frequency "cluster mask" pulses drop groups on/off so the far layers never
 * read as a repeating tile, and scene fog dissolves the far edge into the sky. Snow reuses the
 * same shader with a round-fleck texture, a slow scroll, and gentle sideways sway.
 */
import * as THREE from "three";
import type { WeatherColumn } from "@hyperlight/shared";
import { WEATHER_MAP_GLSL, type WeatherField } from "../world/weather/weatherField";
import { WEATHER_SURFACE_GLSL, type WeatherSurface } from "../world/weather/weatherSurface";

const RADIUS = 120;
const HEIGHT = 220;
/** Cylinder geometry's own axis (three.js builds it along +Y). */
const UP_Y = new THREE.Vector3(0, 1, 0);
const TEX = 256;

/**
 * A tileable, pre-MOTION-BLURRED rain texture: soft vertical smears (transparent→bright→
 * transparent along their length) at varied length/width/opacity. `lighter` compositing lets
 * overlaps build up softly. Each smear is stamped in a 3×3 neighbourhood so anything crossing an
 * edge reappears on the far side — seamless under RepeatWrapping. The shader reads the alpha.
 */
function rainDropTexture(): THREE.CanvasTexture {
  const c = document.createElement("canvas");
  c.width = c.height = TEX;
  const g = c.getContext("2d")!;
  g.clearRect(0, 0, TEX, TEX);
  g.globalCompositeOperation = "lighter";
  const stamp = (x: number, y: number, len: number, w: number, a: number): void => {
    const grad = g.createLinearGradient(0, y, 0, y + len);
    grad.addColorStop(0, "rgba(255,255,255,0)");
    grad.addColorStop(0.4, `rgba(255,255,255,${a})`);
    grad.addColorStop(0.6, `rgba(255,255,255,${a})`);
    grad.addColorStop(1, "rgba(255,255,255,0)");
    g.fillStyle = grad;
    g.fillRect(x - w / 2, y, w, len);
  };
  for (let i = 0; i < 85; i++) {
    const x = Math.random() * TEX;
    const y = Math.random() * TEX;
    const len = 22 + Math.random() * 66;
    const w = 1.1 + Math.random() * 2.4;
    const a = 0.14 + Math.random() * 0.34;
    for (const ox of [-TEX, 0, TEX]) for (const oy of [-TEX, 0, TEX]) stamp(x + ox, y + oy, len, w, a);
  }
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  return tex;
}

/** Tileable soft round flecks — the snow drops (same 3×3 stamp for seamless wrap). */
function snowDotTexture(): THREE.CanvasTexture {
  const c = document.createElement("canvas");
  c.width = c.height = TEX;
  const g = c.getContext("2d")!;
  g.clearRect(0, 0, TEX, TEX);
  g.globalCompositeOperation = "lighter";
  const stamp = (x: number, y: number, r: number, a: number): void => {
    const grad = g.createRadialGradient(x, y, 0, x, y, r);
    grad.addColorStop(0, `rgba(255,255,255,${a})`);
    grad.addColorStop(1, "rgba(255,255,255,0)");
    g.fillStyle = grad;
    g.beginPath();
    g.arc(x, y, r, 0, 6.2832);
    g.fill();
  };
  for (let i = 0; i < 140; i++) {
    const x = Math.random() * TEX;
    const y = Math.random() * TEX;
    const r = 1.4 + Math.random() * 3.2;
    const a = 0.18 + Math.random() * 0.4;
    for (const ox of [-TEX, 0, TEX]) for (const oy of [-TEX, 0, TEX]) stamp(x + ox, y + oy, r, a);
  }
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  return tex;
}

const VERT = /* glsl */ `
  varying vec2 vUv;
  varying vec3 vWorld;
  #include <common>
  #include <logdepthbuf_pars_vertex>
  #include <fog_pars_vertex>
  void main() {
    vUv = uv;
    vWorld = (modelMatrix * vec4(position,1.0)).xyz;
    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    gl_Position = projectionMatrix * mvPosition;
    #include <logdepthbuf_vertex>
    #include <fog_vertex>
  }
`;

const FRAG = /* glsl */ `
  ${WEATHER_MAP_GLSL}
  ${WEATHER_SURFACE_GLSL}
  varying vec3 vWorld;
  uniform float uWeatherOn, uCloudBase;
  uniform vec3 uWeatherWind;
  uniform sampler2D uMap, uRainMap, uSnowMap;
  vec4 columnWeather;
  uniform vec3 uColor;
  uniform float uOpacity;
  uniform float uTime;
  uniform float uScroll;  // vertical fall speed (rain fast, snow slow)
  uniform float uWind;    // sideways drift
  varying vec2 vUv;
  #include <common>
  #include <logdepthbuf_pars_fragment>
  #include <fog_pars_fragment>

  float vhash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123); }
  float vnoise(vec2 p) {
    vec2 i = floor(p), f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    float a = vhash(i), b = vhash(i + vec2(1.0, 0.0));
    float c = vhash(i + vec2(0.0, 1.0)), d = vhash(i + vec2(1.0, 1.0));
    return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
  }
  // Low-frequency mask that pulses drop CLUSTERS on/off so far layers never read as a tile.
  // Sampled along a circle in u (cos/sin) so it stays seamless around the cylinder.
  float cluster(float u, float v, float ph) {
    float a = u * 6.2831853;
    return smoothstep(0.30, 0.85, vnoise(vec2(cos(a), sin(a)) * 1.7 + vec2(v * 2.3, ph)));
  }
  // One parallax layer: repeat rx (integer → seamless wrap) around, vy up the height, scroll sp.
  float layer(float rx, float vy, float sp, float idx) {
    float azimuth = vUv.x * 6.2831853;
    float drift = uWeatherOn > 0.5 ? dot(uWeatherWind.xz, vec2(cos(azimuth), -sin(azimuth))) * 0.08 : uWind;
    vec2 uv = vec2(
      vUv.x * rx + drift * (vUv.y * 2.0 + uTime * (0.25 + 0.15 * idx)) + idx * 0.37,
      vUv.y * vy + uTime * uScroll * sp
    );
    float texel;
    if(uWeatherOn>0.5) {
      vec2 snowUv=vec2(uv.x,vUv.y*vy+uTime*0.26*sp);
      vec2 rainUv=vec2(uv.x,vUv.y*vy+uTime*1.2*sp);
      texel=mix(texture2D(uRainMap,rainUv).a,texture2D(uSnowMap,snowUv).a,columnWeather.a);
    } else texel=texture2D(uMap,uv).a;
    return texel * cluster(vUv.x, vUv.y, uTime * 0.06 * sp + idx * 3.1);
  }

  void main() {
    vec3 local=(uWxInvPlanet*vec4(vWorld,1.0)).xyz;
    columnWeather = uWeatherOn>0.5 ? weatherMap(local) : vec4(0.0);
    if(uWeatherOn>0.5 && (columnWeather.g<0.003 || length(local)-uWxRadius>uCloudBase+40.0)) discard;
    float acc =
        layer(6.0,  2.0, 1.00, 0.0) * 1.00
      + layer(9.0,  3.0, 0.78, 1.0) * 0.80
      + layer(13.0, 4.0, 0.60, 2.0) * 0.62
      + layer(18.0, 6.0, 0.45, 3.0) * 0.48;
    acc = clamp(acc, 0.0, 1.0);
    // Dense at eye level, dissolve toward the top rim (and a touch at the very bottom).
    float band = smoothstep(1.0, 0.42, vUv.y) * smoothstep(0.0, 0.10, vUv.y);
    float alpha = acc * band * uOpacity;
    if(uWeatherOn>0.5) {
      alpha *= columnWeather.g * (1.0-smoothstep(uCloudBase,uCloudBase+40.0,length(local)-uWxRadius));
      // Sparse far streaks; bulk precipitation haze is integrated by the volume.
      alpha *= 0.32;
    }
    if (alpha < 0.004) discard;
    #include <logdepthbuf_fragment>
    gl_FragColor = vec4(uColor, alpha);
    #include <fog_fragment>
  }
`;

const RAIN_COLOR = new THREE.Color(0xacc4dd);
const SNOW_COLOR = new THREE.Color(0xdfe8f2);

export class WeatherVeil {
  private readonly mesh: THREE.Mesh;
  private readonly material: THREE.ShaderMaterial;
  private readonly rainMap: THREE.CanvasTexture;
  private readonly snowMap: THREE.CanvasTexture;
  private intensity = 0; // eased 0..1
  private curKind = 0; // 0 none, 1 rain, 2 snow
  private clock = 0;
  // scratch for anchoring the curtain to the planet's interpolated pose
  private readonly _localUp = new THREE.Vector3();
  private readonly _spinInv = new THREE.Quaternion();
  private readonly _align = new THREE.Quaternion();

  constructor(scene: THREE.Scene) {
    this.rainMap = rainDropTexture();
    this.snowMap = snowDotTexture();
    this.material = new THREE.ShaderMaterial({
      uniforms: THREE.UniformsUtils.merge([
        THREE.UniformsLib.fog,
        {
          uMap: { value: this.rainMap },
          uColor: { value: RAIN_COLOR.clone() },
          uOpacity: { value: 0 },
          uTime: { value: 0 },
          uScroll: { value: 1.2 },
          uWind: { value: 0.05 },
          uWeatherOn: { value: 0 }, uCloudBase: { value: 1e9 },
          uWeatherWind: { value: new THREE.Vector3() },
        },
      ]),
      vertexShader: VERT,
      fragmentShader: FRAG,
      transparent: true,
      depthWrite: false,
      side: THREE.BackSide,
      fog: true,
    });
    // merge() deep-clones uniform values, so re-point the texture uniforms at our live textures.
    this.material.uniforms.uMap.value = this.rainMap;
    this.material.uniforms.uRainMap = { value: this.rainMap };
    this.material.uniforms.uSnowMap = { value: this.snowMap };
    const geo = new THREE.CylinderGeometry(RADIUS, RADIUS, HEIGHT, 48, 1, true);
    this.mesh = new THREE.Mesh(geo, this.material);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 5; // before the near particle box (6), after terrain/atmosphere
    this.mesh.visible = false;
    scene.add(this.mesh);
  }

  setEnvironment(surface: WeatherSurface, field: WeatherField, cloudBase: number, light: THREE.Color, weather: WeatherColumn): void {
    const u = this.material.uniforms;
    Object.assign(u, surface.uniforms, field.uniforms);
    u.uWeatherOn.value = 1;
    u.uCloudBase.value = cloudBase;
    u.uColor.value.copy(light);
    this._spinInv.copy(this.mesh.quaternion).invert();
    u.uWeatherWind.value.set(weather.windx, weather.windy, weather.windz)
      .transformDirection(surface.uniforms.uWxInvPlanet.value.clone().invert())
      .applyQuaternion(this._spinInv).multiplyScalar(weather.windSpeed);
    // Same wind strength as the near field; orientation is resolved below.
    u.uWind.value = weather.windSpeed * 0.06;
  }

  /**
   * @param kind    0 none, 1 rain, 2 snow (at the viewer)
   * @param precip  0..1 intensity at the viewer
   * @param camPos  camera position in RENDER space (the curtain pins to it)
   * @param up      world-space "up" at the viewer (radial from the planet centre), or null
   *                off-planet. The curtain's axis aligns to this so streaks fall along true
   *                gravity instead of world-Y.
   * @param spin    the planet's INTERPOLATED spin quaternion (same one the terrain renders at),
   *                or null off-planet. The curtain's azimuth is locked to it so the streaks ride
   *                the surface rigidly — no drift/jitter against the fast orbit+spin. Matches the
   *                near-field precip's "store local, rebuild through the interpolated pose" idiom.
   * @param dt      frame seconds
   */
  update(
    kind: number,
    precip: number,
    camPos: THREE.Vector3,
    up: THREE.Vector3 | null,
    spin: THREE.Quaternion | null,
    dt: number,
  ): void {
    this.clock = (this.clock + dt) % 3600; // bounded so the shader's uTime keeps float precision
    const spatial = this.material.uniforms.uWeatherOn.value > 0.5;
    const target = spatial ? (up ? 1 : 0) : kind === 0 ? 0 : Math.min(1, precip);
    this.intensity += (target - this.intensity) * (1 - Math.exp(-2 * dt));
    if (kind !== 0) this.curKind = kind;
    else if (spatial && this.curKind === 0) this.curKind = 1;
    if (this.intensity < 0.01 || this.curKind === 0) {
      this.mesh.visible = false;
      return;
    }
    // Swap look per kind (rain smears vs snow flecks) only on change.
    const snow = this.curKind === 2;
    const wantMap = snow ? this.snowMap : this.rainMap;
    const u = this.material.uniforms;
    if (u.uMap.value !== wantMap) {
      u.uMap.value = wantMap;
      if (!spatial) (u.uColor.value as THREE.Color).copy(snow ? SNOW_COLOR : RAIN_COLOR);
      u.uScroll.value = snow ? 0.26 : 1.2; // snow drifts down slowly
      u.uWind.value = snow ? 0.14 : 0.05; // …with more sideways sway
    }
    u.uTime.value = this.clock;
    u.uOpacity.value = (snow ? 0.34 : 0.52) * this.intensity;
    this.mesh.position.copy(camPos);
    // Anchor to the planet: align the cylinder axis to local up and lock its azimuth to the
    // planet's interpolated spin, so the streak texture rides the surface as one rigid frame
    // (no shear/drift against the fast orbit+spin). When up/spin are absent (off-planet, still
    // easing out) keep the last orientation while it fades.
    if (up && spin) {
      this._spinInv.copy(spin).invert();
      this._localUp.copy(up).applyQuaternion(this._spinInv).normalize(); // up in the planet's local frame
      this._align.setFromUnitVectors(UP_Y, this._localUp); // stable-azimuth tilt within that frame
      this.mesh.quaternion.copy(spin).multiply(this._align); // …then spin to world → rides the ground
    }
    this.mesh.visible = true;
  }

  dispose(): void {
    this.mesh.removeFromParent();
    this.mesh.geometry.dispose();
    this.material.dispose();
    this.rainMap.dispose();
    this.snowMap.dispose();
  }
}
